import { XMarkIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useState } from 'react';
import { isDuitNowMethod } from '../constants/duitnow.const';
import type { PaymentConsent } from '../types/payment';
import { expiryMethodName } from '../utils/payment-method-view';

// Configuration constants
const CONSENT_VALIDITY_MONTHS = 6; // Payment consent validity period
const WARNING_THRESHOLD_DAYS = 10; // Show warning when expiry is within this many days

interface ExpiryInfoBarProps {
  paymentMethods: PaymentConsent[] | undefined;
}

const ExpiryInfoBar: React.FC<ExpiryInfoBarProps> = ({ paymentMethods }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Reset dismissal when payment methods change
    setIsDismissed(false);
  }, [paymentMethods]);

  // Early return if no payment methods
  if (!paymentMethods || paymentMethods.length === 0) {
    return null;
  }

  const getExpiringMethods = () => {
    const now = new Date();
    const expiringMethods: Array<{
      method: PaymentConsent;
      expiryType: 'card' | 'consent';
      daysLeft: number;
      isExpired: boolean;
    }> = [];

    paymentMethods.forEach((method) => {
      // PENDING rows (e.g. an unapproved DuitNow request) have no live consent yet.
      if (method.status === 'PENDING') {
        return;
      }

      // expiry_date drives the expiry bar for DuitNow rows only (spec §7.5): other methods
      // (e.g. Stripe-registered cards) already carry their own expiry_date for unrelated
      // purposes, and must keep the created_at + CONSENT_VALIDITY_MONTHS fallback unchanged.
      const isDuitNow = isDuitNowMethod(method.method);

      let consentExpiryDate: Date;
      if (isDuitNow && method.expiry_date) {
        consentExpiryDate = new Date(method.expiry_date);
      } else {
        const createdDate = new Date(method.created_at);
        consentExpiryDate = new Date(createdDate);
        consentExpiryDate.setMonth(
          consentExpiryDate.getMonth() + CONSENT_VALIDITY_MONTHS
        );
      }
      const daysToConsentExpiry = Math.ceil(
        (consentExpiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );

      // Show warning for expired or expiring within threshold
      if (daysToConsentExpiry <= WARNING_THRESHOLD_DAYS) {
        expiringMethods.push({
          method,
          expiryType: method.method === 'CARD' ? 'card' : 'consent',
          daysLeft: daysToConsentExpiry,
          isExpired: daysToConsentExpiry < 0
        });
      }
    });

    return expiringMethods;
  };

  const expiringMethods = getExpiringMethods();

  if (expiringMethods.length === 0 || isDismissed) {
    return null;
  }

  const generateMessage = () => {
    if (expiringMethods.length === 1) {
      const { method, expiryType, daysLeft, isExpired } = expiringMethods[0];
      const methodName = expiryMethodName(method);

      if (isExpired) {
        const daysExpired = Math.abs(daysLeft);
        if (expiryType === 'card') {
          return `Your consent to ${methodName} expired ${daysExpired} day${daysExpired !== 1 ? 's' : ''} ago. Please delete and re-add your payment method to avoid any interruptions.`;
        } else {
          return `Your consent to ${methodName} payment consent expired ${daysExpired} day${daysExpired !== 1 ? 's' : ''} ago. Please delete and re-add your payment method to avoid any interruptions`;
        }
      } else {
        if (expiryType === 'card') {
          return `Your consent to ${methodName} expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Please delete and re-add your payment method to avoid any interruptions.`;
        } else {
          return `Your consent to ${methodName} payment consent expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Please delete and re-add your payment method to avoid any interruptions`;
        }
      }
    } else {
      const expiredMethods = expiringMethods.filter((m) => m.isExpired);
      const expiringSoonMethods = expiringMethods.filter((m) => !m.isExpired);

      let message = '';

      // Handle expired methods first (more urgent)
      if (expiredMethods.length > 0) {
        message += `${expiredMethods.length} payment method${expiredMethods.length !== 1 ? 's have' : ' has'} expired. `;
      }

      // Handle expiring soon methods
      if (expiringSoonMethods.length > 0) {
        message += `${expiringSoonMethods.length} payment method${expiringSoonMethods.length !== 1 ? 's' : ''} expire soon. `;
      }

      message +=
        'Please delete and re-add your payment method to avoid any interruptions';
      return message;
    }
  };

  const hasExpiredMethods = expiringMethods.some((method) => method.isExpired);

  if (expiringMethods.length === 0 || isDismissed) {
    return null;
  }

  // Use red styling for expired methods, orange for expiring soon
  const colorScheme = hasExpiredMethods ? 'red' : 'orange';

  return (
    <div
      className={`mx-4 mb-4 flex items-center justify-between rounded-lg border p-3 sm:mx-6 lg:mx-8 ${
        colorScheme === 'red'
          ? 'border-red-200 bg-red-50'
          : 'border-orange-200 bg-orange-50'
      }`}
    >
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${colorScheme === 'red' ? 'text-red-400' : 'text-orange-400'}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            {hasExpiredMethods ? (
              // X icon for expired methods
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                clipRule="evenodd"
              />
            ) : (
              // Warning icon for expiring soon
              <path
                fillRule="evenodd"
                d="M8.485 3.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.178 2.625-1.516 2.625H3.72c-1.337 0-2.189-1.458-1.515-2.625L8.485 3.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            )}
          </svg>
        </div>
        <div className="ml-3">
          <p
            className={`text-sm font-medium ${
              colorScheme === 'red' ? 'text-red-800' : 'text-orange-800'
            }`}
          >
            {generateMessage()}
          </p>
        </div>
      </div>
      <div className="flex-shrink-0">
        <button
          type="button"
          className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
            colorScheme === 'red'
              ? 'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50'
              : 'bg-orange-50 text-orange-500 hover:bg-orange-100 focus:ring-orange-600 focus:ring-offset-orange-50'
          }`}
          onClick={() => setIsDismissed(true)}
        >
          <span className="sr-only">Dismiss</span>
          <XMarkIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

export default ExpiryInfoBar;
